import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Card,
  Button,
  Typography,
  Space,
  Col,
  Row,
  message,
  Divider,
} from "antd";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { useUserApi } from "../../api/user/userApp";
import { TLessonAnswerUser, TLessonQuestion } from "../../models/courses/lesson";
import { useTranslation } from "react-i18next";
import { useTilmiAuth } from "../../hooks/useTilmiAuth";
import { CODIFICATION_TYPE_USERS } from "../../constant/codification";
import "./forms.css";

const { Title, Text } = Typography;

// ---------- Types locaux ----------
type DragItem = {
  id: string;
  content: string;
  isCorrect: boolean;
};

type SlotItem = {
  id: string;      // answer.id as string, empty when none
  content: string; // text
};

// ---------- Utilitaires ----------
const shuffleArray = <T,>(arr: T[]): T[] =>
  arr
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);

const isWildcard = (s: string) => s.trim() === "*";

// ---------- Props ----------
interface DragAndDropProps {
  question: TLessonQuestion;
  onNextLesson: () => void;
  // When true, force teacher preview regardless of auth and skip persistence
  previewTeacher?: boolean;
}

// ---------- Composant ----------
const DragAndDrop: React.FC<DragAndDropProps> = ({ question, onNextLesson, previewTeacher }) => {
  const { saveQuestionAnswers } = useUserApi();
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.dir(i18n.language) || (typeof document !== 'undefined' ? document.dir : 'ltr') || 'ltr') === 'rtl';
  const { getCodeTypeUser } = useTilmiAuth();
  const isTeacher = !!previewTeacher || (getCodeTypeUser() === CODIFICATION_TYPE_USERS.TEACHER);

  const totalAnswers = question.answers.length;
  const correctAnswersCount = question.answers.filter(a => a.is_correct).length;
  const correctAnswers = question.answers.filter(a => a.is_correct);

  // --- State réponses/slots ---
  // For teachers: pre-fill slots with correct answers, remove them from dragItems
  // For students: start with empty slots, all answers in dragItems
  const [dragItems, setDragItems] = useState<DragItem[]>(() => {
    if (isTeacher) {
      // For teachers: only show incorrect answers in dragItems (correct ones are pre-filled in slots)
      return shuffleArray(
        question.answers
          .filter(a => a.content !== "*" && !a.is_correct)
          .map(a => ({ id: String(a.id), content: a.content, isCorrect: a.is_correct }))
      );
    }
    // For students: all answers (except wildcards) in dragItems
    return shuffleArray(
      question.answers
        .filter(a => a.content !== "*")
        .map(a => ({ id: String(a.id), content: a.content, isCorrect: a.is_correct }))
    );
  });

  const [dropSlots, setDropSlots] = useState<SlotItem[]>(() => {
    if (isTeacher) {
      // For teachers: pre-fill slots with correct answers
      return correctAnswers.map(answer => ({
        id: String(answer.id),
        content: answer.content, // Pre-filled with correct answer
      }));
    }
    // For students: start with empty slots
    return question.answers.filter(a => a.is_correct).map(() => ({ id: "", content: "" }));
  });

  // --- State contrôle ---
  const [onSave, setOnSave] = useState(false);

  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [slotDimensions, setSlotDimensions] = useState<{ width: number; height: number }[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  useLayoutEffect(() => {
    if (isDragging) return;

    if (slotRefs.current.length !== dropSlots.length) {
      slotRefs.current = slotRefs.current.slice(0, dropSlots.length);
    }

    const next = slotRefs.current.map((node) => {
      if (!node) return { width: 0, height: 0 };
      const rect = node.getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    });

    setSlotDimensions(next);
  }, [dropSlots, isDragging]);

  // --- Actions slots ---
  const removeAnswerFromSlot = useCallback(
    (slotIndex: number) => {
      setDropSlots(prev => {
        const current = prev[slotIndex];
        if (!current?.id) return prev;
        const updated = [...prev];
        updated[slotIndex] = { id: "", content: "" };
        // restituer l'item au pool
        setDragItems(items => [
          ...items,
          {
            id: current.id,
            content: current.content,
            isCorrect:
              !!question.answers.find(a => String(a.id) === current.id)?.is_correct,
          },
        ]);
        return updated;
      });
    },
    [question.answers]
  );

  // --- Drag end ---
  const onDragEnd = useCallback(
    (result: DropResult) => {
      setIsDragging(false);
      if (!result.destination) return;
      const { destination, draggableId, source } = result;

      // Déposer dans un slot
      if (destination.droppableId.startsWith("slot-")) {
        const slotIndex = parseInt(destination.droppableId.split("-")[1], 10);
        const dragged = dragItems.find(it => it.id === draggableId);
        if (!dragged) return;

        setDropSlots(prev => {
          const updated = [...prev];

          // S'il y avait déjà une réponse dans ce slot → la remettre dans la liste
          const existing = updated[slotIndex];
          if (existing?.id) {
            setDragItems(items => [
              ...items,
              {
                id: existing.id,
                content: existing.content,
                isCorrect:
                  !!question.answers.find(a => String(a.id) === existing.id)?.is_correct,
              },
            ]);
          }

          updated[slotIndex] = { id: dragged.id, content: dragged.content };
          return updated;
        });

        // Retirer de la liste
        setDragItems(prev => prev.filter(it => it.id !== draggableId));
        return;
      }

      // Re-order dans la liste de droite
      if (source.droppableId === "answers" && destination.droppableId === "answers") {
        const reordered = Array.from(dragItems);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setDragItems(reordered);
      }
    },
    [dragItems, question.answers]
  );

  // --- Validation ---
  const handleValidation = useCallback(async () => {
    // In teacher preview, do not persist; simply advance
    if (previewTeacher) {
      onNextLesson();
      return;
    }
    // vérifier si toutes les bonnes réponses sont correctement placées
    const correct = question.answers.filter(a => a.is_correct);

    // si configuration bizarre (aucune bonne réponse définie)
    if (correctAnswersCount === 0 || totalAnswers === 0) {
      message.error(t("exercise.messages.invalidConfig"));
      return;
    }

    const allCorrect = correct.every((ans, i) => {
      const slot = dropSlots[i];
      const slotContent = (slot?.content ?? "").trim().toLowerCase();
      if (isWildcard(ans.content)) return true;
      return slotContent !== "" && slotContent === ans.content.trim().toLowerCase();
    });

    if (!allCorrect) {
      // ❌ Mauvaise réponse : uniquement message, pas de blocage, pas de tentatives
      message.error(t("exercise.messages.wrongAnswer"));
      return;
    }

    // ✅ Bonne réponse → on garde le même comportement qu'avant : save + next lesson
    setOnSave(true);

    const payload: TLessonAnswerUser[] = dropSlots.map((slot) => ({
      id: 0,
      userapp_id: 0,
      question_id: question.id,
      answer_id: parseInt(slot.id || "0", 10),
      user_input: slot.content,
    }));

    try {
      await saveQuestionAnswers(payload);
      onNextLesson();
    } catch (err: any) {
      message.error(
        err?.response?.data ?? err?.message ?? t("message.bugBackend")
      );
    } finally {
      setOnSave(false);
    }
    // eslint-disable-next-line 
  }, [
    correctAnswersCount,
    totalAnswers,
    dropSlots,
    question.id,
    question.answers,
    onNextLesson,
    saveQuestionAnswers,
    t,
  ]);

  // --- Rendu question + slots (gauche) ---
  const renderQuestionWithSlots = useCallback(() => {
    const lines = (question.content || "").split("\n");

    // Support placeholders in AR/RTL too:
    // Accept patterns "%1", "1%", using ASCII percent '%' or Arabic percent '٪' (U+066A)
    // and digits 0-9, Arabic-Indic (U+0660–U+0669) and Extended Arabic-Indic (U+06F0–U+06F9)
    const isPercent = (ch: string) => ch === '%' || ch === '\u066A';
    const toAsciiDigit = (ch: string): string | null => {
      const code = ch.codePointAt(0) ?? 0;
      // ASCII 0-9
      if (code >= 48 && code <= 57) return ch;
      // Arabic-Indic ٠..٩ (U+0660..U+0669)
      if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
      // Extended Arabic-Indic ۰..۹ (U+06F0..U+06F9)
      if (code >= 0x06f0 && code <= 0x06f9) return String(code - 0x06f0);
      return null;
    };
    const getSlotIndexFromToken = (token: string): number | null => {
      const s = token.trim();
      if (s.length !== 2) return null;
      const a = s[0];
      const b = s[1];
      const db = toAsciiDigit(b);
      const da = toAsciiDigit(a);
      if (isPercent(a) && db !== null) {
        const n = parseInt(db, 10);
        return isFinite(n) ? n - 1 : null;
      }
      if (da !== null && isPercent(b)) {
        const n = parseInt(da, 10);
        return isFinite(n) ? n - 1 : null;
      }
      return null;
    };
    // Split capturing single digit with optional percent on either side so we keep tokens
    const captureToken = /([%\u066A]?[0-9\u0660-\u0669\u06F0-\u06F9][%\u066A]?)/;

    return (
      <div className="q-left-panel" dir={isRTL ? 'rtl' : 'ltr'}>
        <Title level={4} className="q-title">
          {t("exercise.questionTitle")}
        </Title>

        <Space direction="vertical" size="middle" className="q-lines">
          {lines.map((line, lineIndex) => {
            const parts = line.split(captureToken);
            return (
              <div key={lineIndex} className="q-line">
                {parts.map((part, idx) => {
                  const slotIdx = getSlotIndexFromToken(part);
                  if (slotIdx !== null) {
                    return (
                      <span key={`slot-${lineIndex}-${idx}`} className="q-slot-wrap">
                        <Droppable droppableId={`slot-${slotIdx}`}>
                          {(provided) => (
                            <div
                              ref={(el) => {
                                provided.innerRef(el);
                                slotRefs.current[slotIdx] = el;
                              }}
                              {...provided.droppableProps}
                              className={`q-slot-box ${
                                dropSlots[slotIdx]?.content ? "filled" : ""
                              }`}
                              title={dropSlots[slotIdx]?.content || t("exercise.dropHere")}
                              style={
                                isDragging &&
                                slotDimensions[slotIdx] &&
                                slotDimensions[slotIdx]?.width > 0 &&
                                slotDimensions[slotIdx]?.height > 0
                                  ? {
                                      width: slotDimensions[slotIdx].width,
                                      height: slotDimensions[slotIdx].height,
                                    }
                                  : undefined
                              }
                            >
                              {dropSlots[slotIdx]?.content ? (
                                <span className="q-slot-text">{dropSlots[slotIdx]?.content}</span>
                              ) : (
                                <span className="q-slot-placeholder">{t("exercise.dropHere")}</span>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>

                        {dropSlots[slotIdx]?.content && (
                          <button
                            type="button"
                            onClick={() => removeAnswerFromSlot(slotIdx)}
                            className="q-slot-remove"
                            aria-label="Retirer la réponse du slot"
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    );
                  }
                  return (
                    <span key={`t-${lineIndex}-${idx}`} className="q-text">
                      {part}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </Space>

        <Divider />
        <div className="q-actions">
          <Button
            type="primary"
            shape="round"
            size="large"
            onClick={handleValidation}
            loading={onSave}
          >
            {t("button.submit")}
          </Button>
        </div>
      </div>
    );
    // eslint-disable-next-line 
  }, [
    question.content,
    dropSlots,
    removeAnswerFromSlot,
    handleValidation,
    onSave,
    isDragging,
    slotDimensions,
    t,
  ]);

  // --- Rendu réponses disponibles (droite, vertical) ---
  const renderAnswersPool = useCallback(() => {
    return (
      <div className="q-right-panel" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card
          bordered={false}
          className="q-right-card"
          title={<Text strong>{t("exercise.answersTitle")}</Text>}
        >
          <Droppable droppableId="answers" direction="vertical">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="q-answers-list"
              >
                {dragItems.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="q-answer-chip"
                        title={item.content}
                        style={provided.draggableProps.style as React.CSSProperties}
                      >
                        <span className="q-answer-text">{item.content}</span>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {dragItems.length === 0 && (
                  <Text type="secondary" className="q-empty-pool">
                    {t("exercise.emptyPool")}
                  </Text>
                )}
              </div>
            )}
          </Droppable>
        </Card>
      </div>
    );
    // eslint-disable-next-line 
  }, [dragItems, t]);

  // --- Layout global: 2 colonnes (G: question | D: réponses) ---
  return (
    <DragDropContext
      onDragStart={() => setIsDragging(true)}
      onDragEnd={onDragEnd}
    >
      <div className="q-wrapper">
        <Row gutter={[24, 24]} className="q-grid">
          <Col xs={24} md={16}>
            <Card className="q-card">{renderQuestionWithSlots()}</Card>
          </Col>
          <Col xs={24} md={8}>
            {renderAnswersPool()}
          </Col>
        </Row>
      </div>
    </DragDropContext>
  );
};

export default DragAndDrop;
